import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/Button';
import Input from '@/components/Input';
import Loading from '@/components/Loading';
import { Tag } from '@/components/Tag';
import { showErrorToast, showSuccessToast } from '@/features/agent/job_change_processes/lib/toast';
import useCreateCandidateMailTemplate from '../api/useCreateCandidateMailTemplate';
import useFetchCandidateMailTemplates from '../api/useFetchCandidateMailTemplates';
import useUpdateCandidateMailTemplate from '../api/useUpdateCandidateMailTemplate';
import MailActionBar from '../components/MailActionBar';
import MailVariableEditor, {
  type MailVariableEditorHandle,
} from '../components/MailVariableEditor';
import VariableInsertMenu from '../components/VariableInsertMenu';
import useCandidateMailNavigation from '../hooks/useCandidateMailNavigation';

type CandidateMailTemplateFormTemplateProps = {
  templateId?: number;
  sourceTemplateId?: number;
};

export default function CandidateMailTemplateFormTemplate({
  templateId,
  sourceTemplateId,
}: CandidateMailTemplateFormTemplateProps) {
  const isEdit = templateId !== undefined;
  const navigation = useCandidateMailNavigation();
  const { data: templates, isLoading, isError, refetch } = useFetchCandidateMailTemplates();
  const { mutateAsync: createTemplate, isPending: isCreating } = useCreateCandidateMailTemplate();
  const { mutateAsync: updateTemplate, isPending: isUpdating } = useUpdateCandidateMailTemplate();
  const isSubmitting = isCreating || isUpdating;

  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [isInitialized, setIsInitialized] = useState(!isEdit && sourceTemplateId === undefined);
  const bodyEditorRef = useRef<MailVariableEditorHandle>(null);
  const submittingRef = useRef(false);

  const baseTemplate = templates?.find(
    (template) => template.id === (templateId ?? sourceTemplateId)
  );

  useEffect(() => {
    if (isInitialized || templates === undefined) return;
    if (baseTemplate) {
      setName(isEdit ? baseTemplate.name : `${baseTemplate.name}のコピー`);
      setSubject(baseTemplate.subject);
      setBody(baseTemplate.body);
    }
    setIsInitialized(true);
  }, [baseTemplate, isEdit, isInitialized, templates]);

  const isUnchanged =
    isEdit &&
    baseTemplate !== undefined &&
    name === baseTemplate.name &&
    subject === baseTemplate.subject &&
    body === baseTemplate.body;
  const isSaveDisabled = isSubmitting || name.trim().length === 0 || isUnchanged;

  const handleCancel = () => {
    if (templateId !== undefined) {
      void navigation.openTemplate(templateId);
      return;
    }
    void navigation.openFolder('templates');
  };

  const handleSubmit = async () => {
    // isPending は再描画まで反映されず、連打で二重 POST になるため ref で同期的に弾く
    if (isSaveDisabled || submittingRef.current) return;
    submittingRef.current = true;
    const values = { name: name.trim(), subject: subject.trim(), body };
    try {
      if (templateId !== undefined) {
        await updateTemplate({ id: templateId, ...values });
        showSuccessToast('テンプレートを更新しました');
        void navigation.openTemplate(templateId, { replace: true });
      } else {
        const created = await createTemplate(values);
        showSuccessToast('テンプレートを保存しました');
        void navigation.openTemplate(created.id, { replace: true });
      }
    } catch (error) {
      submittingRef.current = false;
      showErrorToast(
        isEdit ? 'テンプレートの更新に失敗しました' : 'テンプレートの保存に失敗しました'
      );
    }
  };

  if (isEdit && !isLoading && isInitialized && !baseTemplate) {
    return (
      <div className="flex w-full flex-col items-center gap-md p-lg py-24">
        <p className="text-body-sm text-sub">テンプレートが見つかりません。</p>
        <Button
          type="button"
          color="secondary"
          size="md"
          onClick={() => void navigation.openFolder('templates')}
        >
          テンプレート一覧へ戻る
        </Button>
      </div>
    );
  }

  if (!isInitialized && isError) {
    return (
      <div className="flex w-full flex-col items-center gap-md p-lg py-24">
        <p className="text-body-sm text-sub">テンプレートの取得に失敗しました。</p>
        <Button type="button" color="secondary" size="md" onClick={() => void refetch()}>
          再読み込み
        </Button>
      </div>
    );
  }

  if (!isInitialized) {
    return (
      <div className="flex w-full justify-center py-24">
        <Loading size="small" />
      </div>
    );
  }

  return (
    <>
      <div className="flex min-h-[calc(100vh-60px)] w-full flex-col gap-md bg-gray p-lg pb-[94px]">
        <div className="flex flex-col gap-xs rounded-sm border border-solid border-border bg-white p-md">
          <div className="flex items-center gap-xs">
            <label htmlFor="candidate-mail-template-name" className="text-heading-xxs text-main">
              テンプレート名
            </label>
            <Tag color="danger" size="sm" className="font-bold!">
              必須
            </Tag>
          </div>
          <Input
            id="candidate-mail-template-name"
            className="w-full"
            placeholder="テンプレート名を入力"
            value={name}
            onChange={(event) => setName(event.target.value)}
            disabled={isSubmitting}
          />
        </div>
        <div className="flex min-h-[460px] flex-1 flex-col overflow-hidden rounded-sm border border-solid border-border bg-white focus-within:border-primary">
          <input
            aria-label="件名"
            placeholder="件名"
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
            disabled={isSubmitting}
            className="h-11 w-full shrink-0 border-x-0 border-t-0 border-b border-solid border-border bg-white px-sm text-body-sm text-main outline-hidden placeholder:text-sub"
          />
          <MailVariableEditor
            ref={bodyEditorRef}
            aria-label="本文"
            placeholder="本文"
            value={body}
            onChange={setBody}
            disabled={isSubmitting}
            className="min-h-0 flex-1"
          />
        </div>
      </div>
      <MailActionBar
        start={
          <VariableInsertMenu
            onInsert={(token) => bodyEditorRef.current?.insert(token)}
            disabled={isSubmitting}
          />
        }
        end={
          <>
            <Button
              type="button"
              color="secondary"
              size="md"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              キャンセル
            </Button>
            <Button
              type="button"
              color="primary"
              size="md"
              onClick={() => void handleSubmit()}
              disabled={isSaveDisabled}
            >
              {isEdit ? '変更を保存' : '保存'}
            </Button>
          </>
        }
      />
    </>
  );
}
